import React, { FC, createContext, ReactNode } from "react";
import { IRoutes } from "@Libs/Router/types";
import { isEmpty, omit } from "lodash-es";
import { buildRouteFromChunks } from "../utils";
import { IAppRoute } from "@App/types";
import { IRouteOptions } from "../types/routes";
import { usePath } from "@Libs/Router/hooks/usePath";

export type IGenerateRouteKey =
  | IAppRoute[]
  | IAppRoute
  | {
      fragments: IAppRoute[] | IAppRoute;
      options?: Omit<IRouteOptions, "matchAny">;
    };

interface IRoutesProviderProps {
  routes: IRoutes;
  children: ReactNode;
}

export type IRouteFunction = (
  fragments: IAppRoute | IAppRoute[],
  options?: IRouteOptions
) => string;

export type IGenerateRouteParams = Record<string, string>;
export type IGenerateRouteQueryParams = IGenerateRouteParams;

export type IGenerateRouteFunction = (
  fragmentsOrRouteArgs: IGenerateRouteKey,
  params?: IGenerateRouteParams,
  queryParams?: IGenerateRouteQueryParams
) => string;

export const RoutesContext = createContext<{
  routes?: IRoutes;
  setRoutes?: (routes: IRoutes) => void;
  route?: IRouteFunction;
  generateRoute?: IGenerateRouteFunction;
}>({});

export const RoutesProvider: FC<IRoutesProviderProps> = ({
  children,
  routes: propsRoutes,
}) => {
  const [routes, setRoutes] = React.useState<IRoutes>(propsRoutes);

  const { generatePath } = usePath();

  const route: IRouteFunction = (fragments, options) => {
    options = { withLocale: false, matchAny: false, ...options };
    const arrayKeys = Array.isArray(fragments) ? fragments : [fragments];
    const chunks: string[] = arrayKeys
      .map((fragment) => routes[fragment])
      .filter((route) => !isEmpty(route));

    return buildRouteFromChunks(chunks, options);
  };

  const generateRoute: IGenerateRouteFunction = (
    fragmentsOrRouteArgs,
    params?,
    queryParams?
  ): string => {
    let path: string;
    if (
      typeof fragmentsOrRouteArgs === "object" &&
      !Array.isArray(fragmentsOrRouteArgs)
    ) {
      const options = omit(fragmentsOrRouteArgs.options, ["matchAny"]);
      path = route(
        fragmentsOrRouteArgs.fragments,
        isEmpty(options) ? undefined : options
      );
    } else {
      path = route(fragmentsOrRouteArgs);
    }
    return generatePath(path, params, queryParams);
  };

  return (
    <RoutesContext.Provider value={{ routes, setRoutes, generateRoute, route }}>
      {children}
    </RoutesContext.Provider>
  );
};
